import './card-component.scss';
import { BridgePosition, Card, CardDeck, Rank, SeatData } from '../../app/types';
import { Sets } from './sets';
import { bridgeWarGui, environment } from '../../env';
import { cardBackgrounds } from '../../app/defaults';
import { classNames } from '../../utils/mixed';
import { outputActions } from '../../slices/outputSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import React, { HTMLAttributes, useCallback, useState } from 'react';

export type CardComponentProps = {
    card: Card;
    hand: Pick<SeatData, 'isInteractive' | 'isVisible'>;
    selected?: boolean;
    setSelected?: (card?: Card) => void;
};

export const CardComponent: React.FC<CardComponentProps> = ({
    card,
    hand: { isVisible: handIsVisible, isInteractive: handIsInteractive },
    selected,
    setSelected,
}) => {
    const { forceShowAllCards } = useAppSelector((state) => state.table);
    const { highlighted, visible } = card;
    const [userHighlighted, setUserHighlighted] = useState(false);
    const showCardBack = visible !== undefined ? !visible : forceShowAllCards ? false : !handIsVisible;
    const onUserHighlight = useCallback(() => {
        if (!showCardBack && !highlighted) {
            setUserHighlighted(true);
        }
    }, [showCardBack, highlighted]);
    const onUserDeHighlight = useCallback(() => {
        if (!showCardBack && !highlighted) {
            setUserHighlighted(false);
        }
    }, [showCardBack, highlighted]);
    const props: HTMLAttributes<HTMLDivElement> = {
        ...(handIsInteractive
            ? {
                  onMouseDown: onUserHighlight,
                  onMouseUp: onUserDeHighlight,
                  onMouseLeave: onUserDeHighlight,
              }
            : {}),
    };
    return showCardBack ? (
        <CardBackComponent />
    ) : (
        <section
            className={classNames('CardComponent', environment === 'shark' && 'isShark', bridgeWarGui && 'isBridgeWar')}
            {...props}
        >
            <CardFaceComponent
                card={card}
                highlighted={highlighted || userHighlighted}
                isInteractive={handIsInteractive}
                selected={selected}
                setSelected={setSelected}
            />
        </section>
    );
};

export const CardFaceComponent: React.FC<
    {
        highlighted?: boolean;
        isInteractive: boolean;
    } & Pick<CardComponentProps, 'card' | 'setSelected' | 'selected'>
> = ({ highlighted, card, isInteractive, selected, setSelected }) => {
    const { contract } = useAppSelector((state) => state.table);
    const {
        settings: { bidLayConfirm },
        sharkSettings: { interactionCard },
    } = useAppSelector((state) => state.app);
    const dispatch = useAppDispatch();
    const { output_setCard } = outputActions;
    const {
        settings: { fourColorCards },
        sharkLayout: { selectedCardSet },
    } = useAppSelector((state) => state.app);
    const playInstantly =
        (environment === 'audrey' && !bidLayConfirm) || (environment === 'shark' && interactionCard === 'instant');
    const handleClick = () => {
        if (isInteractive) {
            if (playInstantly || selected) {
                dispatch(output_setCard(card));
                setSelected?.(undefined);
            } else {
                setSelected?.(card);
            }
        }
    };
    const selectedCardDeck: CardDeck = Sets[selectedCardSet];
    const isTrumpCard = bridgeWarGui && card?.suit && card?.suit === (contract?.strain ?? 'noTrump')
    return (
        <div
            id={`${card.cardPosition}-origin-${card.id}`}
            className={classNames(
                'card face',
                highlighted && 'highlighted',
                isInteractive && 'interactive',
                isTrumpCard && 'trump_card',
                card.rank,
                card.suit,
            )}
            onClick={handleClick}
            style={
                environment === 'audrey'
                    ? {
                          backgroundImage: `url("card_set/${fourColorCards ? 'color/' : ''}${card.id}.png")`,
                      }
                    : {}
            }
        >
            {environment === 'shark' &&
                (!!card.suit && !!card.rank ? (
                    <>
                        {selectedCardDeck.ranks}
                        {selectedCardDeck[card.suit].suits}
                        {[Rank.king, Rank.queen, Rank.jack].indexOf(card.rank) !== -1 && (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            <img src={`${selectedCardDeck[card.suit][card.rank]}`} className="face" alt="" />
                        )}
                    </>
                ) : (
                    <>{selectedCardDeck.ranks}</>
                ))}
        </div>
    );
};



export const CardBackComponent: React.FC<{ bridgePosition?: BridgePosition }> = ({ bridgePosition }) => {
    const { cardBackground } = useAppSelector((state) => state.table);
    return (
        <section
            className={classNames(
                'CardComponent CardBackComponent ',
                bridgePosition && `bridgePosition-${bridgePosition}`,
                bridgeWarGui && 'isBridgeWar',
            )}
        >
            <div
                className="card"
                style={{ backgroundImage: `url("${cardBackgrounds[cardBackground] || cardBackgrounds[0]}")` }}
            />
        </section>
    );
};

export const CardPlaceholderComponent: React.FC = () => {
    return (
        <section className="CardComponent">
            <div className={classNames('card', 'noBackground')} />
        </section>
    );
};
